import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import styles from './Layout.module.css'

const Layout = ({ children }) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const navigation = [
    { name: 'Dashboard', href: '/', icon: '📊' },
    { name: 'Components', href: '/components', icon: '🔧' },
    { name: 'Orders', href: '/orders', icon: '📦' },
  ]

  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  const getNavItemClass = (href) => {
    return `${styles.navItem} ${isActiveRoute(href) ? styles.navItemActive : ''}`
  }

  return (
    <div className={styles.layout}>
      {/* Sidebar for desktop */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarContent}>
          {/* Logo */}
          <div className={styles.logo}>
            <div className={styles.logoIcon}>
              <span className={styles.logoText}>TL</span>
            </div>
            <div className={styles.logoDetails}>
              <h1 className={styles.appName}>TuidaLab</h1>
              <p className={styles.appVersion}>v1.0.0</p>
            </div>
          </div>

          {/* Navigation */}
          <div className={styles.navigation}>
            <nav className={styles.navList}>
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={getNavItemClass(item.href)}
                >
                  <span className={styles.navIcon}>{item.icon}</span>
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* User menu */}
          <div className={styles.userMenu}>
            <div className={styles.userInfo}>
              <div className={styles.userAvatar}>
                <span className={styles.userInitial}>
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className={styles.userDetails}>
                <p className={styles.userName}>
                  {user?.username || 'User'}
                </p>
                <p className={styles.userEmail}>
                  {user?.email || 'user@example.com'}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className={styles.logoutButton}
                title="Logout"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div 
            className={styles.mobileMenuOverlay} 
            onClick={toggleMobileMenu} 
          />
          <div className={styles.mobileMenuPanel}>
            <div className={styles.mobileMenuContent}>
              {/* Mobile logo */}
              <div className={styles.logo}>
                <div className={styles.logoIcon}>
                  <span className={styles.logoText}>CO</span>
                </div>
                <div className={styles.logoDetails}>
                  <h1 className={styles.appName}>Component Orders</h1>
                </div>
              </div>

              {/* Mobile navigation */}
              <div className={styles.navigation}>
                <nav className={styles.navList}>
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={getNavItemClass(item.href)}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className={styles.navIcon}>{item.icon}</span>
                      {item.name}
                    </Link>
                  ))}
                </nav>
              </div>

              {/* Mobile user menu */}
              <div className={styles.userMenu}>
                <div className={styles.userInfo}>
                  <div className={styles.userAvatar}>
                    <span className={styles.userInitial}>
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className={styles.userDetails}>
                    <p className={styles.userName}>
                      {user?.username || 'User'}
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className={styles.logoutButton}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={styles.mainContent}>
        {/* Top bar */}
        <div className={styles.mobileHeader}>
          <button
            type="button"
            className={styles.menuToggle}
            onClick={toggleMobileMenu}
          >
            <span className="sr-only">Open sidebar</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Page content */}
        <main className={styles.pageContent}>
          <div className={styles.pageContainer}>
            <div className={styles.contentWrapper}>
              {/* Page header */}
              <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>
                  {getPageTitle(location.pathname)}
                </h1>
                <p className={styles.pageDescription}>
                  {getPageDescription(location.pathname)}
                </p>
              </div>

              {/* Main content area */}
              <div className={styles.contentArea}>
                {children}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

// Helper function to get page title based on route
const getPageTitle = (pathname) => {
  const routes = {
    '/': 'Dashboard',
    '/components': 'Components',
    '/components/new': 'Add New Component',
    '/components/edit': 'Edit Component',
    '/orders': 'Orders',
    '/orders/new': 'Create New Order',
    '/orders/edit': 'Edit Order',
    '/profile': 'Profile'
  }

  if (pathname.startsWith('/components/edit/')) {
    return 'Edit Component'
  }

  if (pathname.startsWith('/orders/edit/')) {
    return 'Edit Order'
  }

  return routes[pathname] || 'Component Orders'
}

// Helper function to get page description based on route
const getPageDescription = (pathname) => {
  const descriptions = {
    '/': 'Welcome to your component orders dashboard',
    '/components': 'Manage all your electronic components',
    '/components/new': 'Add a new component to your inventory',
    '/components/edit': 'Update component details',
    '/orders': 'View and manage all your orders',
    '/orders/new': 'Create a new component order',
    '/orders/edit': 'Update order details',
    '/profile': 'Manage your account settings'
  }

  if (pathname.startsWith('/components/edit/')) {
    return 'Update component details'
  }

  if (pathname.startsWith('/orders/edit/')) {
    return 'Update order details'
  }

  return descriptions[pathname] || 'Manage your component orders efficiently'
}

export default Layout